import { z } from 'zod';
import { HttpError } from './HttpError';

// バックエンドのエラーレスポンス（RFC 9457 Problem Details）のうち、
// 画面のエラーメッセージ表示に用いるフィールドのみを検証して取り出す。
const problemDetailSchema = z.object({
  detail: z.string().min(1),
  // バリデーションエラー時のみ存在する。zod スキーマ由来の個別メッセージを持つ。
  // この項目の不正（空文字 message・message 欠落・非配列等）でスキーマ全体が失敗すると
  // 有効な detail まで失われるため、不正時は catch で「無し」に落とし detail を生かす。
  // 選別（非空 message の優先）は extractProblemDetail 側で行う。
  validationErrors: z
    .array(z.object({ message: z.string() }))
    .optional()
    .catch(undefined),
});

/**
 * エラーからユーザー提示可能なメッセージを取り出す。
 * バリデーションエラー個別メッセージ（validationErrors の最初の非空 message）を detail より優先して返す。
 * HttpError でない場合、5xx の場合、レスポンスボディが Problem Details 形式でない場合は null を返す。
 */
export const extractProblemDetail = (error: unknown): string | null => {
  if (!(error instanceof HttpError)) return null;
  // 5xx のレスポンスはサーバー内部の情報（外部 API のエラーメッセージ等）を含み得るため、
  // 内容にかかわらずユーザーには露出しない（CWE-209）。呼び出し側の固定文言で表示される。
  if (error.status >= 500) return null;
  const parsed = problemDetailSchema.safeParse(error.body);
  if (!parsed.success) return null;
  // バリデーションエラーの detail は英語の汎用文（"Request data validation failed"）のため、
  // 日本語の個別メッセージ（最初の非空のもの）があればそちらを優先する。
  const firstMessage = parsed.data.validationErrors?.find(e => e.message !== '')?.message;
  return firstMessage ?? parsed.data.detail;
};
